import React, { useCallback, useEffect, useRef, useState } from "react";
import Icon from "@mdi/react";
import {
	mdiPlus,
	mdiPlusCircleOutline,
	mdiArrowUpBold,
	mdiArrowDownBold,
	mdiAlertCircleOutline,
	mdiClose,
	mdiInformationOutline,
	mdiPauseCircleOutline,
	mdiPlayCircleOutline,
} from "@mdi/js";
import {
	CircularProgress,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	TextField,
	LinearProgress,
	Tooltip,
	Menu,
	MenuItem,
	ListItemIcon,
	ListItemText,
	Drawer,
	Divider,
	Chip,
} from "@mui/material";
import { HireMemberModal } from "components";
import type { PublicTeamMemberDTO, SeniorityLevel } from "shared/services";
import { WorkspaceServices } from "shared/services";
import { NotistackServices } from "shared/services";
import { useTeamPageContext } from "./TeamPageContext";
import styles from "./MembersPage.module.scss";

const SENIORITY_LABEL: Record<SeniorityLevel, string> = {
	junior: "Júnior",
	pleno: "Pleno",
	senior: "Sênior",
};

const SENIORITY_ORDER: SeniorityLevel[] = ["junior", "pleno", "senior"];

function getInitials(name?: string): string {
	if (!name) return "AI";
	return name
		.trim()
		.split(/\s+/)
		.filter(Boolean)
		.slice(0, 2)
		.map((chunk) => chunk.charAt(0).toUpperCase())
		.join("");
}

function getRole(member: PublicTeamMemberDTO): string {
	return member.memberLibrary?.name ?? member.activeMcps?.[0]?.mcp?.name ?? member.mcps?.[0]?.mcp?.name ?? "Especialista IA";
}

function formatTokens(n: number): string {
	if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
	if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
	return String(n);
}

function formatRenewalLabel(tokensResetAt: string | null | undefined): string | null {
	if (!tokensResetAt) return null;
	const resetDate = new Date(tokensResetAt);
	const diffMs = resetDate.getTime() - Date.now();
	if (diffMs <= 0) return "Renova em instantes";
	const days = Math.ceil(diffMs / (24 * 60 * 60 * 1000));
	if (days === 1) return "Renova amanhã";
	if (days <= 30) return `Renova em ${days} dias`;
	return `Renova em ${resetDate.toLocaleDateString("pt-BR")}`;
}

function TokenBar({ member }: { member: PublicTeamMemberDTO }) {
	const { tokensUsed = 0, tokensBonus = 0, tokenLimit } = member;
	const effective = tokenLimit !== null && tokenLimit > 0 ? tokenLimit + tokensBonus : null;
	if (effective === null || effective <= 0) return null;

	const pct = Math.min(100, Math.round((tokensUsed / effective) * 100));
	const isNearLimit = pct >= 80;
	const isReached = member.tokenLimitReached;
	const renewalLabel = formatRenewalLabel(member.tokensResetAt);

	return (
		<div className={styles.tokenBar}>
			<div className={styles.tokenBarLabels}>
				<span className={isReached ? styles.tokenLimitWarning : undefined}>
					{isReached ? (
						<>
							<Icon path={mdiAlertCircleOutline} size={0.55} style={{ verticalAlign: "middle", marginRight: 2 }} />
							Limite atingido
						</>
					) : (
						`${formatTokens(tokensUsed)} / ${formatTokens(effective)} tokens`
					)}
				</span>
				<span className={styles.tokenPct}>{pct}%</span>
			</div>
			<LinearProgress
				variant="determinate"
				value={pct}
				color={isReached ? "error" : isNearLimit ? "warning" : "primary"}
				className={styles.tokenProgress}
			/>
			{renewalLabel && (
				<div className={styles.tokenRenewalLabel} title={member.tokensResetAt ?? undefined}>
					{renewalLabel}
				</div>
			)}
		</div>
	);
}

export const MembersPage: React.FC = () => {
	const { workspace, selectedTeam, selectedTeamId, members, loadingMembers, teamNotFound, loadingTeams, reloadMembers } = useTeamPageContext();
	const [hireModalOpen, setHireModalOpen] = useState(false);

	// Add tokens dialog
	const [addTokensTarget, setAddTokensTarget] = useState<PublicTeamMemberDTO | null>(null);
	const [tokenInput, setTokenInput] = useState("");
	const [addingTokens, setAddingTokens] = useState(false);

	// Detail drawer
	const [drawerMember, setDrawerMember] = useState<PublicTeamMemberDTO | null>(null);

	// Promote/demote menu
	const [seniorityAnchor, setSeniorityAnchor] = useState<{ el: HTMLElement; member: PublicTeamMemberDTO } | null>(null);
	const [changingSeniority, setChangingSeniority] = useState(false);

	// EK-455 — observacoes (auto-save com debounce de 800ms)
	const [observationsText, setObservationsText] = useState<string>("");
	const [observationsSaving, setObservationsSaving] = useState(false);
	const [observationsSavedAt, setObservationsSavedAt] = useState<number | null>(null);
	const observationsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	// EK-455 — pausar/reativar
	const [pauseLoading, setPauseLoading] = useState(false);

	// Quando o drawer abre/troca de membro, carrega as observacoes desse membro.
	useEffect(() => {
		if (drawerMember) {
			setObservationsText(drawerMember.observations ?? "");
			setObservationsSavedAt(null);
		}
		return () => {
			if (observationsTimerRef.current) clearTimeout(observationsTimerRef.current);
		};
	}, [drawerMember?.id]); // eslint-disable-line react-hooks/exhaustive-deps

	const handleOpenHireModal = useCallback(() => setHireModalOpen(true), []);
	const handleCloseHireModal = useCallback(() => setHireModalOpen(false), []);
	const handleMemberHired = useCallback(async () => { await reloadMembers(); }, [reloadMembers]);

	const handleOpenAddTokens = useCallback((e: React.MouseEvent, member: PublicTeamMemberDTO) => {
		e.stopPropagation();
		setAddTokensTarget(member);
		setTokenInput("");
	}, []);

	const handleConfirmAddTokens = useCallback(async () => {
		if (!addTokensTarget || !workspace || !selectedTeamId) return;
		const tokens = parseInt(tokenInput, 10);
		if (!Number.isInteger(tokens) || tokens <= 0) {
			NotistackServices.showErrorMessage("Informe um número inteiro positivo de tokens.");
			return;
		}
		setAddingTokens(true);
		try {
			await WorkspaceServices.addMemberTokens(String(workspace.id), String(selectedTeamId), addTokensTarget.id, tokens);
			await reloadMembers();
			setAddTokensTarget(null);
			NotistackServices.showSuccessMessage(`${formatTokens(tokens)} tokens adicionados a ${addTokensTarget.name}.`);
		} catch (err: any) {
			const msg = err?.message ?? err?.code ?? "Falha ao adicionar tokens. Tente novamente.";
			NotistackServices.showErrorMessage(msg);
		} finally {
			setAddingTokens(false);
		}
	}, [addTokensTarget, tokenInput, workspace, selectedTeamId, reloadMembers]);


	const handleOpenSeniorityMenu = useCallback((e: React.MouseEvent<HTMLElement>, member: PublicTeamMemberDTO) => {
		e.stopPropagation();
		setSeniorityAnchor({ el: e.currentTarget, member });
	}, []);

	const handleChangeSeniority = useCallback(async (level: SeniorityLevel) => {
		if (!seniorityAnchor || !workspace || !selectedTeamId) return;
		const { member } = seniorityAnchor;
		setSeniorityAnchor(null);
		if (level === member.seniorityLevel) return;
		setChangingSeniority(true);
		try {
			await WorkspaceServices.updateMember(String(workspace.id), String(selectedTeamId), member.id, { seniority_level: level });
			await reloadMembers();
			NotistackServices.showSuccessMessage(`${member.name} promovido(a) para ${SENIORITY_LABEL[level]}.`);
		} catch (err: any) {
			const msg = err?.message ?? err?.code ?? "Falha ao alterar nível. Tente novamente.";
			NotistackServices.showErrorMessage(msg);
		} finally {
			setChangingSeniority(false);
		}
	}, [seniorityAnchor, workspace, selectedTeamId, reloadMembers]);

	// EK-455 — auto-save das observacoes (debounce 800ms)
	const persistObservations = useCallback(async (memberId: string, text: string) => {
		if (!workspace || !selectedTeamId) return;
		setObservationsSaving(true);
		try {
			await WorkspaceServices.updateMember(String(workspace.id), String(selectedTeamId), memberId, {
				observations: text || null,
			} as any);
			setObservationsSavedAt(Date.now());
			await reloadMembers();
		} catch (err: any) {
			NotistackServices.showErrorMessage(err?.message ?? "Falha ao salvar observações.");
		} finally {
			setObservationsSaving(false);
		}
	}, [workspace, selectedTeamId, reloadMembers]);

	const handleObservationsChange = useCallback((value: string) => {
		if (value.length > 2000) return;
		setObservationsText(value);
		if (!drawerMember) return;
		if (observationsTimerRef.current) clearTimeout(observationsTimerRef.current);
		observationsTimerRef.current = setTimeout(() => {
			persistObservations(drawerMember.id, value);
		}, 800);
	}, [drawerMember, persistObservations]);

	// EK-455 — pausar/reativar
	const handleTogglePause = useCallback(async () => {
		if (!drawerMember || !workspace || !selectedTeamId) return;
		const nextPaused = !drawerMember.isPaused;
		setPauseLoading(true);
		try {
			await WorkspaceServices.updateMember(String(workspace.id), String(selectedTeamId), drawerMember.id, {
				is_paused: nextPaused,
			} as any);
			await reloadMembers();
			NotistackServices.showSuccessMessage(`${drawerMember.name} ${nextPaused ? "pausado(a)" : "reativado(a)"}.`);
			setDrawerMember({ ...drawerMember, isPaused: nextPaused });
		} catch (err: any) {
			NotistackServices.showErrorMessage(err?.message ?? "Falha ao alterar status do membro.");
		} finally {
			setPauseLoading(false);
		}
	}, [drawerMember, workspace, selectedTeamId, reloadMembers]);

	if (!workspace || !selectedTeam) {
		return (
			<div className={styles.stateContainer}>
				<CircularProgress color="inherit" size={24} />
				<span>{teamNotFound ? "Equipe não encontrada." : loadingTeams ? "Carregando equipe..." : "Selecione uma equipe."}</span>
			</div>
		);
	}

	return (
		<>
			<section className={styles.membersPage}>
				<header className={styles.header}>
					<div className={styles.headerMainInfo}>
						<div className={styles.teamAvatar}>{getInitials(selectedTeam.name)}</div>
						<div>
							<h2 className={styles.teamName}>{selectedTeam.name}</h2>
						</div>
					</div>

				</header>

				<div className={styles.contentArea}>
					{loadingMembers || changingSeniority ? (
						<div className={styles.stateContainer}>
							<CircularProgress color="inherit" size={24} />
							<span>{changingSeniority ? "Alterando nível..." : "Carregando membros..."}</span>
						</div>
					) : (
						<div className={styles.membersGrid}>
							{members.map((member) => {
								const seniority: SeniorityLevel = (member.seniorityLevel ?? member.seniority ?? "pleno") as SeniorityLevel;
								const currentIdx = SENIORITY_ORDER.indexOf(seniority);
								const canPromote = currentIdx < SENIORITY_ORDER.length - 1;
								const canDemote = currentIdx > 0;

								return (
									<article
										key={member.id}
										className={`${styles.memberCard} ${member.tokenLimitReached ? styles.memberCardLimitReached : ""}`}
									>
										<header className={styles.memberCardHeader}>
											<h3 className={styles.memberName}>{member.name}</h3>
										</header>

										<div className={styles.memberAvatar}>{getInitials(member.name)}</div>

										<p className={styles.memberRole}>{getRole(member)}</p>
										<p className={styles.memberSeniority}>{SENIORITY_LABEL[seniority]}</p>

										<TokenBar member={member} />

										<div className={styles.memberActions}>
											<Tooltip title="Ver detalhes">
												<button
													type="button"
													className={styles.actionBtn}
													onClick={(e) => { e.stopPropagation(); setDrawerMember(member); }}
												>
													<Icon path={mdiInformationOutline} size={0.7} />
												</button>
											</Tooltip>
											<Tooltip title="Adicionar tokens">
												<button
													type="button"
													className={styles.actionBtn}
													onClick={(e) => handleOpenAddTokens(e, member)}
												>
													<Icon path={mdiPlusCircleOutline} size={0.7} />
												</button>
											</Tooltip>
											{(canPromote || canDemote) && (
												<Tooltip title="Alterar nível">
													<button
														type="button"
														className={styles.actionBtn}
														onClick={(e) => handleOpenSeniorityMenu(e, member)}
													>
														<Icon path={canPromote ? mdiArrowUpBold : mdiArrowDownBold} size={0.7} />
													</button>
												</Tooltip>
											)}
										</div>
									</article>
								);
							})}

							<button
								type="button"
								className={styles.addMemberCard}
								onClick={handleOpenHireModal}
								disabled={!selectedTeamId}
							>
								<div className={styles.addMemberIcon}>
									<Icon path={mdiPlus} size={1.2} />
								</div>
								<p className={styles.addMemberTitle}>Adicionar novo membro</p>
								<p className={styles.addMemberDescription}>Selecione um template para contratar</p>
							</button>
						</div>
					)}
				</div>
			</section>

			{workspace && selectedTeamId && (
				<HireMemberModal
					open={hireModalOpen}
					workspaceId={String(workspace.id)}
					teamId={String(selectedTeamId)}
					onClose={handleCloseHireModal}
					onHired={handleMemberHired}
				/>
			)}

			{/* Dialog: adicionar tokens */}
			<Dialog open={Boolean(addTokensTarget)} onClose={() => setAddTokensTarget(null)} maxWidth="xs" fullWidth>
				<DialogTitle>Adicionar tokens — {addTokensTarget?.name}</DialogTitle>
				<DialogContent>
					<TextField
						autoFocus
						label="Quantidade de tokens"
						type="number"
						fullWidth
						value={tokenInput}
						onChange={(e) => setTokenInput(e.target.value)}
						slotProps={{ htmlInput: { min: 1, step: 1000 } }}
						helperText={
							addTokensTarget?.tokenLimit !== null
								? `Limite atual: ${formatTokens((addTokensTarget?.tokenLimit ?? 0) + (addTokensTarget?.tokensBonus ?? 0))} | Usado: ${formatTokens(addTokensTarget?.tokensUsed ?? 0)}`
								: "Sem limite configurado para este nível."
						}
						sx={{ mt: 1 }}
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setAddTokensTarget(null)} disabled={addingTokens}>Cancelar</Button>
					<Button variant="contained" onClick={handleConfirmAddTokens} disabled={addingTokens || !tokenInput}>
						{addingTokens ? <CircularProgress size={18} /> : "Confirmar"}
					</Button>
				</DialogActions>
			</Dialog>

			{/* Menu: promover / rebaixar */}
			<Menu
				anchorEl={seniorityAnchor?.el}
				open={Boolean(seniorityAnchor)}
				onClose={() => setSeniorityAnchor(null)}
			>
				{SENIORITY_ORDER.filter((lvl) => lvl !== seniorityAnchor?.member.seniorityLevel).map((lvl) => {
					const currentIdx = SENIORITY_ORDER.indexOf(seniorityAnchor?.member.seniorityLevel ?? "pleno");
					const targetIdx = SENIORITY_ORDER.indexOf(lvl);
					const isPromotion = targetIdx > currentIdx;
					return (
						<MenuItem key={lvl} onClick={() => handleChangeSeniority(lvl)}>
							<ListItemIcon>
								<Icon path={isPromotion ? mdiArrowUpBold : mdiArrowDownBold} size={0.75} />
							</ListItemIcon>
							<ListItemText
								primary={isPromotion ? `Promover para ${SENIORITY_LABEL[lvl]}` : `Rebaixar para ${SENIORITY_LABEL[lvl]}`}
							/>
						</MenuItem>
					);
				})}
			</Menu>

			{/* Drawer: detalhes do membro */}
			<Drawer
				anchor="right"
				open={Boolean(drawerMember)}
				onClose={() => setDrawerMember(null)}
				PaperProps={{ className: styles.drawerPaper }}
			>
				{drawerMember && (() => {
					const dm = drawerMember;
					const seniority: SeniorityLevel = (dm.seniorityLevel ?? dm.seniority ?? "pleno") as SeniorityLevel;
					const { tokensUsed = 0, tokensBonus = 0, tokenLimit } = dm;
					const effective = tokenLimit !== null ? tokenLimit + tokensBonus : null;
					const pct = effective !== null ? Math.min(100, Math.round((tokensUsed / effective) * 100)) : null;
					const isNear = (pct ?? 0) >= 80;
					const barColor: "error" | "warning" | "primary" = dm.tokenLimitReached ? "error" : isNear ? "warning" : "primary";

					return (
						<div className={styles.drawerContent}>
							<div className={styles.drawerHeader}>
								<div className={styles.drawerAvatar}>{getInitials(dm.name)}</div>
								<div className={styles.drawerHeaderInfo}>
									<h3>{dm.name}</h3>
									<span>{getRole(dm)}</span>
									{dm.isPaused && (
										<Chip label="Pausado" size="small" color="warning" style={{ marginTop: 4 }} />
									)}
								</div>
								<button className={styles.drawerClose} onClick={() => setDrawerMember(null)}>
									<Icon path={mdiClose} size={0.85} />
								</button>
							</div>

							<Divider />

							<div className={styles.drawerSection}>
								<div className={styles.drawerSectionTitle}>Nível de Senioridade</div>
								<div className={styles.drawerHierarchy}>
									{SENIORITY_ORDER.map((lvl) => (
										<div
											key={lvl}
											className={`${styles.hierarchyItem} ${lvl === seniority ? styles.hierarchyItemActive : ""}`}
										>
											<span className={styles.hierarchyLabel}>{SENIORITY_LABEL[lvl]}</span>
											{dm.tokenLimit !== null && lvl === seniority && (
												<span className={styles.hierarchyLimit}>{formatTokens(dm.tokenLimit)} tk/ciclo</span>
											)}
										</div>
									))}
								</div>
							</div>

							<Divider />

							<div className={styles.drawerSection}>
								<div className={styles.drawerSectionTitle}>Consumo de Tokens</div>

								<div className={styles.drawerStatRow}>
									<span className={styles.drawerStatLabel}>Tokens usados</span>
									<span className={styles.drawerStatValue}>{formatTokens(tokensUsed)}</span>
								</div>
								<div className={styles.drawerStatRow}>
									<span className={styles.drawerStatLabel}>Limite base (nível)</span>
									<span className={styles.drawerStatValue}>{tokenLimit !== null ? formatTokens(tokenLimit) : "Ilimitado"}</span>
								</div>
								{tokensBonus > 0 && (
									<div className={styles.drawerStatRow}>
										<span className={styles.drawerStatLabel}>Bônus adicionado</span>
										<span className={`${styles.drawerStatValue} ${styles.drawerStatBonus}`}>+{formatTokens(tokensBonus)}</span>
									</div>
								)}
								{effective !== null && (
									<div className={styles.drawerStatRow}>
										<span className={styles.drawerStatLabel}>Limite efetivo</span>
										<span className={styles.drawerStatValue}>{formatTokens(effective)}</span>
									</div>
								)}

								{pct !== null && (
									<div className={styles.drawerProgress}>
										<div className={styles.drawerProgressLabels}>
											<span className={dm.tokenLimitReached ? styles.tokenLimitWarning : undefined}>
												{dm.tokenLimitReached ? (
													<><Icon path={mdiAlertCircleOutline} size={0.6} style={{ verticalAlign: "middle", marginRight: 3 }} />Limite atingido</>
												) : `${pct}% utilizado`}
											</span>
											<span>{formatTokens(tokensUsed)} / {formatTokens(effective!)}</span>
										</div>
										<LinearProgress
											variant="determinate"
											value={pct}
											color={barColor}
											className={styles.drawerProgressBar}
										/>
									</div>
								)}

								<div className={styles.drawerChips}>
									<Chip label={SENIORITY_LABEL[seniority]} size="small" variant="outlined" color="primary" />
									{dm.tokenLimitReached && <Chip label="Limite atingido" size="small" color="error" />}
									{tokensBonus > 0 && <Chip label={`+${formatTokens(tokensBonus)} bônus`} size="small" color="success" variant="outlined" />}
								</div>
							</div>

							<Divider />

							{/* EK-455 — Observações com auto-save (debounce 800ms) */}
							<div className={styles.drawerSection}>
								<div className={styles.drawerSectionTitle}>
									Observações
									<span className={styles.drawerObsHint}>
										{observationsSaving
											? "salvando..."
											: observationsSavedAt
												? "salvo"
												: "instruções permanentes lidas pelo agente"}
									</span>
								</div>
								<TextField
									multiline
									minRows={3}
									maxRows={8}
									placeholder="Ex: tom de voz formal, sempre confirmar antes de enviar e-mails, foco em conteúdo de marketing B2B..."
									value={observationsText}
									onChange={(e) => handleObservationsChange(e.target.value)}
									fullWidth
									inputProps={{ maxLength: 2000, "aria-label": "Observações para o membro" }}
									size="small"
								/>
								<div className={styles.drawerObsCount}>{observationsText.length} / 2000</div>
							</div>

							<Divider />

							<div className={styles.drawerSection}>
								<div className={styles.drawerSectionTitle}>Ações</div>
								<div className={styles.drawerActions}>
									<button
										className={styles.drawerActionBtn}
										onClick={(e) => { setDrawerMember(null); handleOpenAddTokens(e as any, dm); }}
									>
										<Icon path={mdiPlusCircleOutline} size={0.75} />
										Adicionar tokens
									</button>
									<button
										className={styles.drawerActionBtn}
										onClick={(e) => { setDrawerMember(null); handleOpenSeniorityMenu(e as any, dm); }}
									>
										<Icon path={mdiArrowUpBold} size={0.75} />
										Alterar nível
									</button>
									{/* EK-455 — Pausar / Reativar */}
									<button
										className={styles.drawerActionBtn}
										onClick={handleTogglePause}
										disabled={pauseLoading}
										aria-label={dm.isPaused ? "Reativar membro" : "Pausar membro"}
									>
										{pauseLoading ? (
											<CircularProgress size={14} />
										) : (
											<Icon path={dm.isPaused ? mdiPlayCircleOutline : mdiPauseCircleOutline} size={0.75} />
										)}
										{dm.isPaused ? "Reativar" : "Pausar"}
									</button>
								</div>
							</div>

							<div className={styles.drawerMeta}>
								<span>Criado: {new Date(dm.createdAt).toLocaleDateString("pt-BR")}</span>
								<span>Atualizado: {new Date(dm.updatedAt).toLocaleDateString("pt-BR")}</span>
							</div>
						</div>
					);
				})()}
			</Drawer>
		</>
	);
};
